import React, { useState } from 'react';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { analyticsLogger, PageType, ReportType } from 'commons/src/analytics';
import { INSIGHT_CREATED_REPORT } from 'commons/src/analytics/AnalyticsEvents';
import PageWrapper from 'commons/src/components/containers/PageWrapper';
import Error from 'commons/src/components/errorComponents/Error';
import PageHeader from 'commons/src/components/headers/PageHeader';
import SubHeader from 'commons/src/components/headers/SubHeader';
import { IntercomAPI } from 'commons/src/components/Intercom';
import { mediumFormLoader } from 'commons/src/components/placeholder';
import { iaqInsightReportDevices, iaqInsightReportSensors } from 'commons/src/DeviceAndSensorLists';
import { SensorTypes } from 'commons/src/models/commonEnums';
import { ErrorType } from 'commons/src/models/commonTypeScript';
import { fetchIaqInsightData } from '../../../actions/insightActions';
import { paths } from '../../../constants';
import { BuildingProps } from '../../../models/commonEnums';
import { Store } from '../../../reducers';
import { BusinessRequestType as RequestType } from '../../../reducers/BusinessRequestType';
import InsightMultipleDevices, { ReportData } from '../reportSelector/InsightMultipleDevices';
import IAQInsightResultView from './IAQInsightResultView';

export const IAQInsightPageComponent = (): React.ReactElement => {
    const { t: txt } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const locationsLoading = useSelector(
        (state: Store) => state.locations.loading && state.locations.locations.length === 0
    );
    const locationsError = useSelector((state: Store) => state.locations.error);
    const fetchReportDataLoading = useSelector(
        (state: Store) => state.requests[RequestType.FetchIaqInsightData]?.loading || false
    );
    const error = useSelector((state: Store) => state.requests[RequestType.FetchIaqInsightData]?.error) as
        | ErrorType
        | undefined;

    const optionalBuildingProps = [BuildingProps.floors, BuildingProps.buildingYear];

    const [reportStartTime, setReportStartTime] = useState<Moment | null>(null);
    const [reportEndTime, setReportEndTime] = useState<Moment | null>(null);
    const [displayResultSection, setDisplayResultSection] = useState(false);

    if (locationsError) {
        return <Error />;
    }

    const numberOfDays =
        reportEndTime && reportStartTime && moment(reportEndTime).diff(moment(reportStartTime), 'days');
    const graphHidden = numberOfDays && numberOfDays > 31;

    const fetchReportData = (reportData: ReportData): void => {
        const { serialNumbers, from, to, sensors, includeLogo } = reportData;
        analyticsLogger(INSIGHT_CREATED_REPORT, {
            pageType: PageType.Insight,
            reportType: ReportType.IAQInsight,
            reportData,
        });

        IntercomAPI('trackEvent', 'generated-IAQ-insight-report', {
            serialNumbers,
            from,
            to,
            sensors,
            logoInReport: includeLogo,
        });
        dispatch(fetchIaqInsightData(reportData));
    };
    const hideGraphProps = {
        header: 'Insight.GraphWillBeHidden',
        subText: 'Insight.GraphHiddenAbove31Days',
        isHidden: !!graphHidden,
    };

    const sensorsNotToAutoSelect: SensorTypes[] = [SensorTypes.mold];

    const goBack = (): void => {
        navigate({ pathname: `/${paths.reports}` });
    };

    return (
        <div className="radon-insight-view">
            <PageHeader title={txt('Insight.IaqAnalysis')} />
            <PageWrapper pageType="wide">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <SubHeader onGoBack={goBack} backTitle="Reports.Reports" actionButtons={[]} />
                    <Link to={`/${paths.iaqInsight2}`} className="text-link" style={{ whiteSpace: 'nowrap' }}>
                        {txt('IAQReport2.SwitchToNewView')}
                    </Link>
                </div>
            </PageWrapper>
            <PageWrapper pageType="medium" whiteBackground>
                <ReactPlaceholder ready={!locationsLoading} customPlaceholder={mediumFormLoader}>
                    <div className="form form__wide-container">
                        <div className="header-2">
                            <span className="text-large">{txt('Insight.IaqAnalysisDescription')}</span>
                        </div>
                        <InsightMultipleDevices
                            setDisplayResultSection={setDisplayResultSection}
                            fetchReportDataLoading={fetchReportDataLoading}
                            fetchReportData={fetchReportData}
                            reportStartTime={reportStartTime}
                            reportEndTime={reportEndTime}
                            setReportStartTime={setReportStartTime}
                            setReportEndTime={setReportEndTime}
                            possibleSensorTypes={iaqInsightReportSensors}
                            acceptedDeviceTypes={iaqInsightReportDevices}
                            minNumberOfDays={0}
                            maxNumberOfDays={182}
                            allowLogo
                            hideGraphProps={hideGraphProps}
                            optionalBuildingProps={optionalBuildingProps}
                            sensorNotToAutoSelect={sensorsNotToAutoSelect}
                            showAllSensors
                            hideSegmentProperties
                        />
                    </div>
                </ReactPlaceholder>
            </PageWrapper>
            {displayResultSection && !error && reportStartTime && reportEndTime && (
                <IAQInsightResultView
                    from={reportStartTime}
                    to={reportEndTime}
                    optionalBuildingProps={optionalBuildingProps}
                />
            )}
        </div>
    );
};

export default IAQInsightPageComponent;
